import { useSuspenseQuery } from '@apollo/client';
import type React from 'react';
import { Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useParams } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { TopLevelPageLink } from '@xing-com/crate-people-directory-top-level-page-link';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { Grid, Row } from '@xing-com/xing-grid';

import { ContentSpacer, StyledHeadline } from './city-page.styles';
import { SEO_URL_META_DATA } from './constants';
import { CityPageDocument } from './graphql/city-page.query.gql-types';

type BasicPageLayoutProps = {
  seoTitle: string;
  seoDescription: string;
  index: boolean;
  seoUrl: string;
  pageName: string;
};

const CityPageInner: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { country, city: citySlug } = useParams();
  const { formatMessage } = useIntl();
  const { isLoggedOut } = useLoginState();

  const { data, error } = useSuspenseQuery(CityPageDocument, {
    variables: { country, city: citySlug },
  });

  useEffect(() => {
    if (!isLoggedOut) {
      window.location.assign('https://www.xing.com/search/members');
    }
  }, [isLoggedOut]);

  if (!isLoggedOut) return;
  if (error || !data) throw error;

  const { letters = [], city, breadcrumbs } = data?.peopleCityPage || {};

  const sanitizedLetters = letters?.map((letter) => {
    return {
      letter: letter?.letter || '',
      url: `${letter?.url}` || '',
      topUserBags: letter?.topUserBags?.map((topUserBag) => {
        return {
          url: `${topUserBag?.url}` || '',
          name: topUserBag?.name || '',
        };
      }),
    };
  });

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  if (!data?.peopleCityPage || !letters?.length) {
    return <EmptyStateFallback {...basicPageLayoutProps} />;
  }

  const headlineMessage = formatMessage(
    {
      id: 'PD_CITIES_PEOPLE_IN_CITY_HEADLINE',
    },
    { city }
  );
  const seoTitle = formatMessage({ id: 'PD_CITIES_CITY_TITLE' }, { city });
  const seoDescription = formatMessage(
    { id: 'PD_CITIES_CITY_META_DESCRIPTION' },
    { city }
  );

  const pageLayoutProps = {
    ...basicPageLayoutProps,
    seoTitle,
    seoDescription,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  return (
    <PageLayout {...pageLayoutProps}>
      <ContentSpacer>
        <StyledHeadline size="xxlarge">{headlineMessage}</StyledHeadline>
        <Grid>
          <Row>
            {sanitizedLetters?.map(({ letter, url, topUserBags }, index) => (
              <TopLevelPageLink
                key={`${letter}-${index}`}
                title={letter}
                url={url}
                exampleNames={topUserBags}
              />
            ))}
          </Row>
        </Grid>
      </ContentSpacer>
    </PageLayout>
  );
};

const ErrorFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.error} />
    </PageLayout>
  );
};

const EmptyStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  return (
    <PageLayout {...basicPageLayoutProps}>
      <UserMessage type={UserMessageType.empty} />
    </PageLayout>
  );
};

export const CityPage: React.FC = () => {
  const { country, city: citySlug } = useParams();
  const { formatMessage } = useIntl();
  const basicPageLayoutProps = {
    seoTitle: formatMessage({ id: 'PD_100_HEADING' }),
    seoDescription: formatMessage({ id: 'PD_100_DESCR' }),
    index: true,
    seoUrl: `${SEO_URL_META_DATA}${country}/${citySlug}`,
    pageName: `cities/${country}/`,
  };

  return (
    <ErrorBoundary onError={() => <ErrorFallback {...basicPageLayoutProps} />}>
      <Suspense fallback={null}>
        <CityPageInner {...basicPageLayoutProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
